/**
 * NavItem - Reusable navigation item for Sidebar and BottomTabs
 *
 * Supports:
 * - Active state styling (teal accent, stroke weight differentiation)
 * - Icons (Lucide React)
 * - Labels (optional for icon-only mode)
 * - Touch targets (48×48px minimum)
 * - Keyboard navigation
 */

import { type LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { T } from '@/styles/tokens';

export interface NavItemProps {
  to: string;
  icon: LucideIcon;
  label: string;
  variant?: 'sidebar' | 'bottom';
  collapsed?: boolean; // For sidebar collapsed state
  onClick?: () => void;
}

export function NavItem({
  to,
  icon: Icon,
  label,
  variant = 'sidebar',
  collapsed = false,
  onClick,
}: NavItemProps) {
  const location = useLocation();
  const isActive = location.pathname === to;

  if (variant === 'bottom') {
    const handleBottomClick = (e: React.MouseEvent) => {
      if (isActive) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      onClick?.();
    };

    return (
      <Link
        to={to}
        onClick={handleBottomClick}
        className="flex-1 flex flex-col items-center justify-center gap-[4px] min-w-touch transition-all"
        style={{ height: '100%' }}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
      >
        <Icon
          className="w-6 h-6 transition-opacity"
          strokeWidth={isActive ? 2.5 : 1.5}
          style={{
            color: isActive ? T.primary : '#C0BAB2',
            opacity: isActive ? 1 : 0.6,
          }}
        />
        <span
          className="transition-colors"
          style={{
            fontSize: 10,
            color: isActive ? T.primary : '#C0BAB2',
            fontWeight: isActive ? 700 : 500,
          }}
        >
          {label}
        </span>
      </Link>
    );
  }

  // Sidebar style (desktop) - teal highlight + bold text for active
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`
        w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs transition-colors
        ${isActive
          ? 'bg-teal-50 text-teal-700 font-semibold border-l-2 border-teal-500'
          : 'text-charcoal-500 hover:bg-cream-200 hover:text-charcoal-700'
        }
      `}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
    >
      <Icon
        className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-teal-600' : ''}`}
        strokeWidth={isActive ? 2.2 : 1.5}
      />
      {!collapsed && (
        <span className="truncate">{label}</span>
      )}
    </Link>
  );
}

export default NavItem;
